import { useRef, useState } from "react";
import * as htmlToImage from "html-to-image";
import { motion } from "framer-motion";
import { Share, Download, X, Trophy, Flag } from "@phosphor-icons/react";

/**
 * Generates a branded PNG of the round summary that mates can drop into a chat.
 * - Solo round → personal scorecard with par diff.
 * - Fourball   → group leaderboard with winner highlight.
 *
 * Uses navigator.share with a File when available (mobile), falls back to download.
 */
export default function ShareRoundCard({ round, course }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState("");

  const isFourball = Array.isArray(round.players) && round.players.length > 1;
  const vsPar = round.total_strokes - round.total_par;
  const vsParLabel = vsPar === 0 ? "Level par" : vsPar > 0 ? `+${vsPar} over` : `${vsPar} under`;

  let winner = null;
  if (isFourball) {
    const ranked = [...round.players]
      .filter((p) => p.scores.length > 0)
      .sort((a, b) => a.total_strokes - b.total_strokes);
    if (ranked.length) {
      const top = ranked[0];
      const tied = ranked.filter((p) => p.total_strokes === top.total_strokes);
      winner = { players: tied, strokes: top.total_strokes };
    }
  }

  const buildBlob = async () => {
    if (!cardRef.current) return null;
    // 2x for sharper output on retina/AMOLED screens
    return await htmlToImage.toBlob(cardRef.current, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: "#143D2F",
    });
  };

  const share = async () => {
    setBusy(true);
    setToast("");
    try {
      const blob = await buildBlob();
      if (!blob) throw new Error("Could not render card");
      const filename = `fairway-${(course.name || "round").replace(/\s+/g, "-").toLowerCase()}.png`;
      const file = new File([blob], filename, { type: "image/png" });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        const acquisitionLink = "https://fairway-golf.org";
        await navigator.share({
          files: [file],
          title: `My round at ${course.name}`,
          text: isFourball
            ? `Fourball at ${course.name} 🏌️ ${winner ? `Winner: ${winner.players.map((p) => p.name).join(" & ")} (${winner.strokes})` : ""}\n\nBeat me on Fairway ⛳ ${acquisitionLink}`
            : `${course.name} · ${round.total_strokes} (${vsParLabel}) — beat that!\n\nGet Fairway (free GPS + scoring) ⛳ ${acquisitionLink}`,
        });
        setToast("Shared!");
      } else {
        // Fallback: download. Web Share API not available on this device.
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        setToast("Saved to downloads");
      }
    } catch (err) {
      // User-cancelled share is a normal flow, not an error
      if (err?.name !== "AbortError") {
        setToast("Could not share — try again");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="share-recap-button"
        className="btn-primary w-full"
      >
        <Share size={18} weight="fill" /> Share round recap
      </button>

      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4"
          style={{ background: "rgba(20,61,47,0.6)" }}
          onClick={() => setOpen(false)}
          data-testid="share-recap-modal"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: "spring", damping: 22, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm relative"
          >
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              data-testid="share-recap-close"
              className="absolute -top-3 -right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center shadow-lg"
              style={{ background: "var(--surface, #F2EEE7)", color: "var(--primary)" }}
            >
              <X size={18} weight="bold" />
            </button>

            {/* The actual card that gets rasterised */}
            <div
              ref={cardRef}
              data-testid="share-recap-card"
              className="rounded-3xl overflow-hidden"
              style={{
                background: "linear-gradient(160deg, #143D2F 0%, #0c2a20 100%)",
                color: "#F2EEE7",
                width: "100%",
                aspectRatio: "9 / 16",
                position: "relative",
                fontFamily: "Inter, system-ui, sans-serif",
              }}
            >
              {/* Decorative ring */}
              <div
                style={{
                  position: "absolute",
                  top: -120,
                  right: -120,
                  width: 360,
                  height: 360,
                  borderRadius: "50%",
                  background: "rgba(196,84,60,0.18)",
                  filter: "blur(2px)",
                }}
              />
              <div style={{ position: "relative", padding: "28px 24px 24px", height: "100%", display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
                  <Flag size={20} weight="fill" color="#C4543C" />
                  <span style={{ fontSize: 13, letterSpacing: 4, textTransform: "uppercase", opacity: 0.7, fontWeight: 700 }}>
                    Fairway
                  </span>
                </div>

                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", opacity: 0.65, fontWeight: 700 }}>
                    {isFourball ? "Group round" : "Solo round"}
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.1, marginTop: 6 }}>
                    {course.name}
                  </div>
                  <div style={{ fontSize: 13, opacity: 0.6, marginTop: 6 }}>
                    Par {round.total_par} · {round.scores.length} holes
                  </div>
                </div>

                {isFourball ? (
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    {winner && (
                      <div
                        style={{
                          background: "rgba(196,84,60,0.18)",
                          border: "1px solid rgba(196,84,60,0.4)",
                          borderRadius: 16,
                          padding: "12px 14px",
                          marginBottom: 14,
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <Trophy size={20} weight="fill" color="#C4543C" />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", opacity: 0.75, fontWeight: 700 }}>
                            Winner
                          </div>
                          <div style={{ fontSize: 17, fontWeight: 800, marginTop: 2 }}>
                            {winner.players.map((p) => p.name).join(" & ")}
                          </div>
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 900, fontVariantNumeric: "tabular-nums" }}>
                          {winner.strokes}
                        </div>
                      </div>
                    )}
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {round.players.map((p) => {
                        const playerVs = p.total_strokes - p.total_par;
                        const isWinner = winner?.players.some((w) => w.player_index === p.player_index);
                        return (
                          <div
                            key={p.player_index}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "10px 14px",
                              borderRadius: 12,
                              background: isWinner ? "rgba(242,238,231,0.14)" : "rgba(242,238,231,0.06)",
                              border: isWinner ? "1px solid rgba(242,238,231,0.3)" : "1px solid transparent",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <div
                                style={{
                                  width: 26,
                                  height: 26,
                                  borderRadius: 8,
                                  background: "rgba(242,238,231,0.12)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 11,
                                  fontWeight: 800,
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                P{p.player_index + 1}
                              </div>
                              <div style={{ fontSize: 15, fontWeight: 700 }}>{p.name}</div>
                            </div>
                            <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                              <div style={{ fontSize: 22, fontWeight: 900, fontVariantNumeric: "tabular-nums" }}>
                                {p.total_strokes || 0}
                              </div>
                              <div style={{ fontSize: 11, opacity: 0.65, fontWeight: 700 }}>
                                {p.scores.length === 0
                                  ? "—"
                                  : playerVs === 0
                                  ? "E"
                                  : playerVs > 0
                                  ? `+${playerVs}`
                                  : playerVs}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
                    <div style={{ fontSize: 14, letterSpacing: 3, textTransform: "uppercase", opacity: 0.7, fontWeight: 700 }}>
                      Final
                    </div>
                    <div
                      style={{
                        fontSize: 120,
                        lineHeight: 1,
                        fontWeight: 900,
                        fontVariantNumeric: "tabular-nums",
                        margin: "4px 0",
                      }}
                    >
                      {round.total_strokes}
                    </div>
                    <div
                      style={{
                        display: "inline-block",
                        padding: "8px 18px",
                        borderRadius: 999,
                        background: vsPar <= 0 ? "rgba(196,84,60,0.25)" : "rgba(242,238,231,0.1)",
                        border: vsPar <= 0 ? "1px solid rgba(196,84,60,0.5)" : "1px solid rgba(242,238,231,0.18)",
                        fontSize: 16,
                        fontWeight: 800,
                        marginTop: 8,
                      }}
                    >
                      {vsParLabel}
                    </div>
                    <div style={{ marginTop: 36, fontSize: 13, opacity: 0.5 }}>Beat me on Fairway</div>
                  </div>
                )}

                <div style={{ marginTop: "auto", fontSize: 11, opacity: 0.45, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 700 }}>
                  fairway · uk golf
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-3">
              <button
                onClick={share}
                disabled={busy}
                data-testid="share-recap-share-btn"
                className="btn-primary flex-1"
              >
                {busy ? "Generating…" : (
                  <>
                    {navigator.canShare ? <Share size={18} weight="fill" /> : <Download size={18} weight="fill" />}
                    {navigator.canShare ? "Share" : "Save image"}
                  </>
                )}
              </button>
            </div>
            {toast && (
              <div
                data-testid="share-recap-toast"
                className="text-center text-xs font-bold mt-2 font-heading"
                style={{ color: "#F2EEE7" }}
              >
                {toast}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </>
  );
}
